import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Loader2, CheckCircle, ExternalLink, Upload, Briefcase, MapPin, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const PROGRESS_STAGES = [
  "Analyzing your resume...",
  "Identifying target companies...",
  "Searching decision-makers...",
  "Building contact profiles...",
  "Finalizing your Inner Circle..."
];

const STAGE_DURATION = 25000;

export default function InnerCircleSearch() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Form fields
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [targetRole, setTargetRole] = useState("");
  const [targetIndustry, setTargetIndustry] = useState("");
  const [locationPreferences, setLocationPreferences] = useState("");
  const [seniorityLevel, setSeniorityLevel] = useState("");
  
  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [reportFound, setReportFound] = useState(false);
  const [foundReportId, setFoundReportId] = useState<string | null>(null);

  // Progress animation
  useEffect(() => {
    if (!isSearching || reportFound) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (PROGRESS_STAGES.length * (STAGE_DURATION / 100)));
        return Math.min(newProgress, 95);
      });
    }, 100);

    const stageInterval = setInterval(() => {
      setCurrentStage(prev => Math.min(prev + 1, PROGRESS_STAGES.length - 1));
    }, STAGE_DURATION);

    const timerInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stageInterval);
      clearInterval(timerInterval);
    };
  }, [isSearching, reportFound]);

  // Poll for results
  const pollForResults = useCallback(async () => {
    if (!foundReportId || reportFound) return;

    try {
      const { data, error } = await supabase
        .from('inner_circle_reports')
        .select('id, status')
        .eq('id', foundReportId)
        .single();

      if (error) {
        console.error('Poll error:', error);
        return;
      }

      if (data && data.status === 'completed') {
        setReportFound(true);
        setProgress(100);
        toast({
          title: "Report Complete!",
          description: "Your Inner Circle Report is ready to view.",
        });
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, [foundReportId, reportFound, toast]);

  useEffect(() => {
    if (!isSearching || !foundReportId || reportFound) return;

    const pollInterval = setInterval(pollForResults, 5000);
    pollForResults();

    return () => clearInterval(pollInterval);
  }, [isSearching, foundReportId, reportFound, pollForResults]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({ title: "Please sign in to generate a report", variant: "destructive" });
      return;
    }

    if (!targetRole.trim()) {
      toast({ title: "Please enter a target role", variant: "destructive" });
      return;
    }

    if (!targetIndustry.trim()) {
      toast({ title: "Please enter a target industry", variant: "destructive" });
      return;
    }

    setIsSearching(true);
    setCurrentStage(0);
    setProgress(0);
    setElapsedTime(0);
    setReportFound(false);
    setFoundReportId(null);

    try {
      const { data, error } = await supabase.functions.invoke('trigger-report', {
        body: {
          action: 'inner_circle',
          target_role: targetRole.trim(),
          target_industry: targetIndustry.trim(),
          location_preferences: locationPreferences.trim(),
          seniority_level: seniorityLevel,
        }
      });

      if (error) throw error;

      if (data?.report_id) {
        setFoundReportId(data.report_id);
      }

      toast({
        title: "Report Started",
        description: "Building your Inner Circle. This typically takes 8-12 minutes.",
      });
    } catch (err: any) {
      console.error('Submit error:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      if (err?.context) {
        try {
          const body = await err.context.json();
          console.error('Error response body:', body);
        } catch (_) {
          console.error('Error context text:', await err.context?.text?.().catch(() => 'N/A'));
        }
      }
      toast({
        title: "Error",
        description: err.message || "Failed to start the report. Please try again.",
        variant: "destructive",
      });
      setIsSearching(false);
      setFoundReportId(null);
    }
  };

  const handleViewReport = () => {
    if (foundReportId) {
      navigate('/inner-circle-reports');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isSearching) {
    return (
      <Card className="border-2 border-indigo-400 bg-white shadow-lg">
        <CardHeader className="text-center bg-indigo-50 border-b border-indigo-200">
          <CardTitle className="flex items-center justify-center gap-2">
            {reportFound ? (
              <>
                <CheckCircle className="w-6 h-6 text-success" />
                Report Complete!
              </>
            ) : (
              <>
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                Building Your Inner Circle...
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{reportFound ? "Complete" : PROGRESS_STAGES[currentStage]}</span>
              <span>{formatTime(elapsedTime)}</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          <div className="space-y-2">
            {PROGRESS_STAGES.map((stage, index) => (
              <div
                key={stage}
                className={`flex items-center gap-2 text-sm transition-all duration-300 ${
                  index < currentStage || reportFound
                    ? 'text-success'
                    : index === currentStage && !reportFound
                    ? 'text-indigo-600 font-medium'
                    : 'text-muted-foreground'
                }`}
              >
                {index < currentStage || reportFound ? (
                  <CheckCircle className="w-4 h-4" />
                ) : index === currentStage && !reportFound ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-muted-foreground/30" />
                )}
                {stage}
              </div>
            ))}
          </div>

          <div className="bg-indigo-50 rounded-lg p-4 text-sm text-muted-foreground border border-indigo-200">
            <p><strong>Target Role:</strong> {targetRole}</p>
            <p><strong>Industry:</strong> {targetIndustry}</p>
            {locationPreferences && (
              <p><strong>Location:</strong> {locationPreferences}</p>
            )}
            {seniorityLevel && (
              <p><strong>Seniority:</strong> {seniorityLevel}</p>
            )}
          </div>

          {reportFound && (
            <Button onClick={handleViewReport} className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700">
              <ExternalLink className="w-4 h-4" />
              View Full Report
            </Button>
          )}

          {!reportFound && (
            <div className="text-center space-y-1">
              <p className="text-sm text-muted-foreground">
                This takes 8-12 minutes. You can leave this page—we'll notify you in your TailorWiz account when your Report is ready.
              </p>
              <Button variant="link" size="sm" onClick={() => navigate('/inner-circle-reports')} className="text-xs text-indigo-600">
                View My Reports →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-indigo-400 bg-white shadow-lg">
      <CardHeader className="bg-indigo-50 border-b border-indigo-200">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-600" />
          Build Your Inner Circle
        </CardTitle>
        <CardDescription>
          Enter your career details to identify 500-1,000 decision-makers
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Resume Upload */}
          <div className="space-y-2">
            <Label htmlFor="resume">Resume (Optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="resume"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="border-2 border-gray-400"
              />
              {resumeFile && (
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Upload className="w-4 h-4" />
                  {resumeFile.name}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Upload your resume for better company matching based on your experience.
            </p>
          </div>

          {/* Target Role */}
          <div className="space-y-2">
            <Label htmlFor="targetRole">Target Role / Title *</Label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="targetRole"
                type="text"
                placeholder="e.g., VP of Marketing, Senior Product Manager"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                className="pl-10 border-2 border-gray-400"
              />
            </div>
          </div>

          {/* Target Industry */}
          <div className="space-y-2">
            <Label htmlFor="targetIndustry">Target Industry *</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="targetIndustry"
                type="text"
                placeholder="e.g., SaaS, Healthcare, Financial Services"
                value={targetIndustry}
                onChange={(e) => setTargetIndustry(e.target.value)}
                className="pl-10 border-2 border-gray-400"
              />
            </div>
          </div>

          {/* Location Preferences */}
          <div className="space-y-2">
            <Label htmlFor="locationPreferences">Location Preferences</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Textarea
                id="locationPreferences"
                placeholder="e.g., San Francisco Bay Area, Remote-first companies, Open to NYC or Austin"
                value={locationPreferences}
                onChange={(e) => setLocationPreferences(e.target.value)}
                className="pl-10 min-h-[80px] border-2 border-gray-400"
              />
            </div>
          </div>

          {/* Seniority Level */}
          <div className="space-y-2">
            <Label htmlFor="seniorityLevel">Seniority Level</Label>
            <Select value={seniorityLevel} onValueChange={setSeniorityLevel}>
              <SelectTrigger className="border-2 border-gray-400">
                <SelectValue placeholder="Select your target seniority level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entry">Entry Level</SelectItem>
                <SelectItem value="mid">Mid-Level</SelectItem>
                <SelectItem value="senior">Senior</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="vp">VP / Executive</SelectItem>
                <SelectItem value="c-suite">C-Suite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Users className="w-4 h-4" />
            Build My Inner Circle
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
